/**
 * @fileoverview Componente responsável por visualizar imagens com suporte a rotação.
 *
 * Exporta também as peças do popup para quem já tem a própria janela: a
 * imagem com girar, ampliar (clique; o ponto ampliado segue o mouse) e baixar
 * (RotatableImageStage), e o download (saveImageBlob, downloadImage). O popup
 * de mídias do painel do contato usa as mesmas peças.
 */

import React, { useState, useEffect, useRef } from "react";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import { IconButton, Tooltip, Dialog } from "@material-ui/core";
import { RotateRight } from "@material-ui/icons";
import api from "../../services/api";

import { X, Download } from "lucide-react";
const useStyles = makeStyles(theme => ({
	messageMedia: {
		objectFit: "cover",
		width: 250,
		height: 200,
		borderTopLeftRadius: 8,
		borderTopRightRadius: 8,
		borderBottomLeftRadius: 8,
		borderBottomRightRadius: 8,
		cursor: "pointer",
		transition: "transform 0.3s ease-in-out",
		display: "block",
	},
	imageContainer: {
		position: "relative",
		display: "block",
		maxWidth: 250,
		width: "fit-content",
	},
	modalDialog: {
		"& .MuiDialog-paper": {
			backgroundColor: "transparent",
			boxShadow: "none",
			maxWidth: "none",
			maxHeight: "none",
			margin: 0,
			position: "relative",
		},
		"& .MuiBackdrop-root": {
			backgroundColor: "rgba(0, 0, 0, 0.7)",
		},
	},
	modalContent: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		height: "100vh",
		width: "100vw",
		position: "relative",
		overflow: "hidden",
	},
	imageWrapper: {
		position: "relative",
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		width: "100%",
		height: "100%",
		cursor: "default",
	},
	modalCloseButton: {
		position: "fixed",
		top: theme.spacing(2),
		right: theme.spacing(2),
		color: "white",
		backgroundColor: "rgba(0, 0, 0, 0.6)",
		zIndex: 1001,
		"&:hover": {
			backgroundColor: "rgba(0, 0, 0, 0.8)",
		},
	},
	modalImage: {
		maxWidth: "90vw",
		maxHeight: "90vh",
		objectFit: "contain",
		transition: "transform 0.3s ease-in-out",
		borderRadius: 8,
		boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
	},
	modalActions: {
		position: "fixed",
		bottom: theme.spacing(3),
		left: "50%",
		transform: "translateX(-50%)",
		display: "flex",
		justifyContent: "center",
		gap: theme.spacing(2),
		zIndex: 1001,
	},
	modalButton: {
		backgroundColor: "rgba(0, 0, 0, 0.6)",
		color: "white",
		border: "none",
		borderRadius: 8,
		padding: theme.spacing(1.5),
		minWidth: 44,
		"&:hover": {
			backgroundColor: "rgba(0, 0, 0, 0.8)",
		},
	},
}));

/** Salva a imagem no computador, com a extensão do tipo dela. */
export const saveImageBlob = (data, contentType) => {
	if (!data) return;

	// Detecta extensão baseado no content-type
	const getExtension = (type) => {
		const extensions = {
			"image/jpeg": "jpg",
			"image/jpg": "jpg",
			"image/png": "png",
			"image/gif": "gif",
			"image/webp": "webp",
			"image/bmp": "bmp",
			"image/svg+xml": "svg"
		};
		return extensions[type] || "jpg";
	};

	const extension = getExtension(contentType);
	const filename = `imagem_${new Date().getTime()}.${extension}`;

	const url = window.URL.createObjectURL(
		new Blob([data], { type: contentType })
	);

	const link = document.createElement("a");
	link.href = url;
	link.download = filename;
	link.style.display = 'none';

	// Adicionar ao DOM de forma segura
	document.body.appendChild(link);

	// Aguardar anexação ao DOM antes de clicar
	setTimeout(() => {
		link.click();

		// Aguardar conclusão do download antes de remover
		setTimeout(() => {
			// Verificar se o nó ainda está no DOM antes de remover
			if (link.parentNode === document.body) {
				document.body.removeChild(link);
			}
			window.URL.revokeObjectURL(url);
		}, 100);
	}, 0);
};

/** Baixa a imagem de um endereço (da API ou já local, "blob:") e salva. */
export const downloadImage = async (src) => {
	if (String(src).startsWith("blob:")) {
		const blob = await fetch(src).then((r) => r.blob());
		saveImageBlob(blob, blob.type);
		return;
	}
	const { data, headers } = await api.get(src, { responseType: "blob" });
	saveImageBlob(data, headers["content-type"]);
};

/**
 * A imagem do popup: girar, ampliar 2,5× com um clique (o ponto ampliado segue
 * o mouse; outro clique volta) e baixar. Girar e ampliar recomeçam a cada
 * imagem (quem usa troca a "key" ao trocar de imagem).
 */
export const RotatableImageStage = ({ src, onBackgroundClick, onDownload, imageClassName, actionsClassName }) => {
	const classes = useStyles();
	const [rotation, setRotation] = useState(0);
	const [isZoomed, setIsZoomed] = useState(false);
	const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
	const imageRef = useRef(null);
	const wrapperRef = useRef(null);

	const handleRotate = () => {
		setRotation(prevRotation => (prevRotation + 90) % 360);
	};

	const getImageStyle = () => {
		const scale = isZoomed ? 2.5 : 1;

		let origin = "center center";

		if (isZoomed) {
			let { x, y } = mousePosition;

			// Compensa a rotação para o transformOrigin
			// Quando rotacionado, os eixos estão trocados/invertidos
			if (rotation === 90) {
				// 90°: X vira Y, Y vira X invertido
				const newX = y;
				const newY = 100 - x;
				x = newX;
				y = newY;
			} else if (rotation === 180) {
				// 180°: ambos invertidos
				x = 100 - x;
				y = 100 - y;
			} else if (rotation === 270) {
				// 270°: X vira Y invertido, Y vira X
				const newX = 100 - y;
				const newY = x;
				x = newX;
				y = newY;
			}

			origin = `${x}% ${y}%`;
		}

		return {
			transform: `rotate(${rotation}deg) scale(${scale})`,
			transformOrigin: origin,
			transition: "transform 0.2s ease-out",
		};
	};

	const handleWrapperClick = (e) => {
		// Clique na área ao redor da imagem
		if (e.target === e.currentTarget && onBackgroundClick) {
			onBackgroundClick();
		}
	};

	const handleImageClick = (e) => {
		e.stopPropagation();
		setIsZoomed(!isZoomed);
	};

	const handleMouseMove = (e) => {
		if (!wrapperRef.current) return;

		const wrapper = wrapperRef.current.getBoundingClientRect();
		const x = ((e.clientX - wrapper.left) / wrapper.width) * 100;
		const y = ((e.clientY - wrapper.top) / wrapper.height) * 100;

		setMousePosition({
			x: Math.max(0, Math.min(100, x)),
			y: Math.max(0, Math.min(100, y))
		});
	};

	return (
		<>
			<div
				ref={wrapperRef}
				className={classes.imageWrapper}
				onMouseMove={handleMouseMove}
				onClick={handleWrapperClick}
			>
				<img
					ref={imageRef}
					className={clsx(classes.modalImage, imageClassName)}
					src={src}
					alt="image"
					style={getImageStyle()}
					onClick={handleImageClick}
					onMouseEnter={(e) => e.currentTarget.style.cursor = isZoomed ? 'zoom-out' : 'zoom-in'}
					loading="lazy"
					decoding="async"
				/>
			</div>

			<div className={clsx(classes.modalActions, actionsClassName)}>
				<Tooltip title="Girar imagem">
					<span>
						<IconButton
							className={classes.modalButton}
							onClick={handleRotate}
						>
							<RotateRight />
						</IconButton>
					</span>
				</Tooltip>
				{onDownload && (
					<Tooltip title="Baixar imagem">
						<span>
							<IconButton
								className={classes.modalButton}
								onClick={onDownload}
							>
								<Download />
							</IconButton>
						</span>
					</Tooltip>
				)}
			</div>
		</>
	);
};

const RotatableImage = ({ imageUrl }) => {
	const classes = useStyles();
	const [fetching, setFetching] = useState(true);
	const [blobUrl, setBlobUrl] = useState("");
	const [imageData, setImageData] = useState(null);
	const [modalOpen, setModalOpen] = useState(false);

	useEffect(() => {
		if (!imageUrl) return;
		const fetchImage = async () => {
			const { data, headers } = await api.get(imageUrl, {
				responseType: "blob",
			});
			const url = window.URL.createObjectURL(
				new Blob([data], { type: headers["content-type"] })
			);
			setBlobUrl(url);
			setImageData({ data, contentType: headers["content-type"] });
			setFetching(false);
		};
		fetchImage();
	}, [imageUrl]);

	// Girar e ampliar recomeçam a cada abertura: a janela desmonta a imagem ao fechar.
	const handleCloseModal = () => {
		setModalOpen(false);
	};

	const handleModalContentClick = (e) => {
		// Fecha o modal se clicar no fundo (fora da imagem)
		if (e.target === e.currentTarget) {
			handleCloseModal();
		}
	};

	return (
		<div className={classes.imageContainer}>
			<img
				className={classes.messageMedia}
				src={fetching ? imageUrl : blobUrl}
				alt="image"
				onClick={() => setModalOpen(true)}
				loading="lazy"
				decoding="async"
			/>

			<Dialog
				open={modalOpen}
				onClose={handleCloseModal}
				fullScreen
				className={classes.modalDialog}
			>
				<div className={classes.modalContent} onClick={handleModalContentClick}>
					<Tooltip title="Fechar">
						<span>
							<IconButton
								className={classes.modalCloseButton}
								onClick={handleCloseModal}
							>
								<X />
							</IconButton>
						</span>
					</Tooltip>

					<RotatableImageStage
						src={fetching ? imageUrl : blobUrl}
						onBackgroundClick={handleCloseModal}
						onDownload={() => imageData && saveImageBlob(imageData.data, imageData.contentType)}
					/>
				</div>
			</Dialog>
		</div>
	);
};

export default RotatableImage;
